// The Gmail OAuth callback's URL handling, and the Sentry redaction that backs it up.
//
// Google requires the redirect URI to be registered on the OAuth client exactly, and
// does not accept wildcards - so this path has to be added to the client's Authorized
// redirect URIs for every origin, including each per-PR QA env.
export const OUTREACH_GMAIL_CALLBACK_PATH = "/leads/google-callback";

export interface OutreachGmailCallbackParams {
  code: string;
  state: string;
}

// sessionStorage, so the code survives a Kinde login bounce: UserContext sends an
// unauthenticated or foreign-token load through a full-page Kinde login and back to
// the current URL, which wipes module memory - and by then the lift has already taken
// the code out of that URL. The route removes the stash immediately before exchanging,
// so a reload or Back after the exchange finds nothing to re-submit. Module memory is
// the fallback for when storage throws (blocked site data, some private modes).
const STORAGE_KEY = "inrange.outreachGmailCallback";
// Google's authorisation codes are short-lived; an older stash can only fail.
const MAX_AGE_MS = 10 * 60 * 1000;
type Stash = OutreachGmailCallbackParams & { at: number };
let memoryStash: Stash | null = null;

const writeStash = (params: OutreachGmailCallbackParams) => {
  memoryStash = { ...params, at: Date.now() };
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(memoryStash));
  } catch {
    // Memory above still covers this page load.
  }
};

const readStash = (): Stash | null => {
  if (memoryStash) return memoryStash;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Stash) : null;
  } catch {
    return null;
  }
};

// Called from index.tsx before Sentry.init and before React mounts. Two jobs:
//
// 1. Move `code` and `state` out of the address bar into the stash above. Everything
//    that reads window.location from here on - Kinde's post-login `redirectTo`,
//    Sentry breadcrumbs and event request URLs, replay's page URLs - sees a clean
//    URL. What it cannot reach is the browser's navigation timing entry, which keeps
//    the URL the document was loaded with: Sentry's pageload request/response spans
//    and replay's navigation entry are named from it, so index.tsx also runs the
//    redaction helpers below over what Sentry sends.
// 2. Re-key Google's `?error=` to `?googleError=`. Kinde v5's provider aborts its
//    whole init on ANY `?error=` param on ANY path - the check sits above its
//    redirect-URI pathname guard - and UserContext then bounces a valid session
//    through a full re-login, so an operator who cancels a GOOGLE prompt would be
//    answered with an InRange login.
export const liftOutreachGmailCallbackParams = (): void => {
  if (window.location.pathname !== OUTREACH_GMAIL_CALLBACK_PATH) return;
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const state = params.get("state");
  const googleError = params.get("error");
  if (!code && !state && !googleError) return;
  if (code && state) writeStash({ code, state });
  params.delete("code");
  params.delete("state");
  if (googleError) {
    params.delete("error");
    params.set("googleError", googleError);
  }
  const search = params.toString();
  // history.state is passed through so react-router's own entry state survives.
  window.history.replaceState(
    window.history.state,
    "",
    `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`
  );
};

// Non-destructive so a StrictMode double render reads the same value both times.
export const peekOutreachGmailCallbackParams =
  (): OutreachGmailCallbackParams | null => {
    const stash = readStash();
    if (
      !stash ||
      typeof stash.code !== "string" ||
      typeof stash.state !== "string" ||
      typeof stash.at !== "number" ||
      Date.now() - stash.at > MAX_AGE_MS
    ) {
      return null;
    }
    return { code: stash.code, state: stash.state };
  };

// Called immediately before the exchange: from then on the code is spent, so no
// remount, reload or Back may find it again.
export const clearOutreachGmailCallbackParams = (): void => {
  memoryStash = null;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing was stored if storage is unavailable.
  }
};

// Every string rather than only callback-path URLs: Kinde's own login callback carries
// a `code` and `state` under the same names, and those are credentials too.
const OAUTH_QUERY_PARAMS = /([?&](?:code|state)=)[^&#\s"]*/g;

export const redactOAuthCallbackParams = (value: string): string =>
  value.replace(OAUTH_QUERY_PARAMS, "$1[Filtered]");

const redactStrings = (record: Record<string, unknown> | undefined) => {
  if (!record) return;
  for (const key of Object.keys(record)) {
    const value = record[key];
    if (typeof value === "string")
      record[key] = redactOAuthCallbackParams(value);
  }
};

// For Sentry's beforeSend / beforeSendTransaction: every field an event carries a
// URL in. Span names land in `description`.
export const redactOAuthCallbackParamsInEvent = <
  T extends {
    request?: { url?: string };
    transaction?: string;
    spans?: { description?: string; data?: Record<string, unknown> }[];
    breadcrumbs?: { message?: string; data?: Record<string, unknown> }[];
    contexts?: { trace?: { data?: Record<string, unknown> } };
  },
>(
  event: T
): T => {
  if (event.request?.url) {
    event.request.url = redactOAuthCallbackParams(event.request.url);
  }
  if (event.transaction) {
    event.transaction = redactOAuthCallbackParams(event.transaction);
  }
  for (const span of event.spans ?? []) {
    if (span.description) {
      span.description = redactOAuthCallbackParams(span.description);
    }
    redactStrings(span.data);
  }
  for (const crumb of event.breadcrumbs ?? []) {
    if (crumb.message) crumb.message = redactOAuthCallbackParams(crumb.message);
    redactStrings(crumb.data);
  }
  redactStrings(event.contexts?.trace?.data);
  return event;
};

// For replay's beforeAddRecordingEvent: performance spans (the navigation entry is
// one, named from the original document URL) and breadcrumbs.
export const redactOAuthCallbackParamsInRecordingEvent = <
  T extends { data?: { payload?: unknown } },
>(
  event: T
): T => {
  const payload = event.data?.payload;
  if (payload && typeof payload === "object") {
    const frame = payload as Record<string, unknown>;
    for (const key of ["description", "message"]) {
      const value = frame[key];
      if (typeof value === "string")
        frame[key] = redactOAuthCallbackParams(value);
    }
    if (frame.data && typeof frame.data === "object") {
      redactStrings(frame.data as Record<string, unknown>);
    }
  }
  return event;
};
